import { Button, Checkbox, Input, Space, TableProps } from "antd";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { useAppSelector } from "../../../../../app/store";
import SearchIcon from "../../../../../assets/icons/SearchIcon";
import CustomModal from "../../../../../components/ui/CustomModal";
import CustomTable from "../../../../../components/ui/CustomTable";
import { useGetAllUsersQuery } from "../../../../../features/portal/user/userApi";
import useError from "../../../../../hooks/useError";
import useTable from "../../../../../hooks/useTable";
import {
  GetAllUsersDataParamsType,
  GetAllUsersDataType,
} from "../../../../../types/portal/user";
import { countParPageSerialNumber } from "../../../../../utils/number";
import { addKeyInArray } from "../../../../../utils/utils";

type IUser = GetAllUsersDataType["data"]["users"];

type Props = {
  open: boolean;
  handleClose: () => void;
  data: IUser;
  setData: Dispatch<SetStateAction<IUser>>;
};

enum Status {
  active = "status-success",
  inactive = "status-danger",
  invited = "status-info",
}

export default function AddUserModal({
  open,
  handleClose,
  data,
  setData,
}: Props) {
  const { user } = useAppSelector((state) => state.auth);

  // -------------------------
  // STATE
  // -------------------------
  const [selectedUsers, setSelectedUsers] = useState<IUser>([]);

  // Load preselected users
  useEffect(() => {
    if (open) {
      setSelectedUsers(data || []);
    }
  }, [open, data]);

  const {
    page,
    limit,
    handlePagination,
    handleLimit,
    filter,
    keyword,
    handleSearch,
  } = useTable<GetAllUsersDataParamsType>();

  const {
    isError,
    error,
    isLoading,
    isFetching,
    data: employeeData,
  } = useGetAllUsersQuery({
    params: {
      ...filter,
      skip: undefined,
      page,
      search: keyword,
      status: "active",
      company_id: user?.data?.company_id,
    },
  });
  useError(isError, error, "Get all user fail");

  const users = employeeData?.data?.users || [];

  // -------------------------
  // CHECKBOX LOGIC
  // -------------------------
  const isChecked = (id: number) =>
    selectedUsers.some((item) => item.id === id);

  const handleToggle = (row: any) => {
    setSelectedUsers((prev) => {
      if (prev.some((item) => item.id === row.id)) {
        return prev.filter((item) => item.id !== row.id); // remove
      }
      return [...prev, row]; // add
    });
  };

  // Select all on current page
  const handleSelectAllPage = (checked: boolean) => {
    if (checked) {
      const newSelection = [
        ...selectedUsers,
        ...users.filter((u: any) => !isChecked(u.id)),
      ];
      setSelectedUsers(newSelection);
    } else {
      const filtered = selectedUsers.filter(
        (u) => !users.some((usr) => usr.id === u.id)
      );
      setSelectedUsers(filtered);
    }
  };

  const allCheckedCurrentPage =
    users.length > 0 && users.every((u: any) => isChecked(u.id));
  const someCheckedCurrentPage =
    users.some((u: any) => isChecked(u.id)) && !allCheckedCurrentPage;

  // -------------------------
  // TABLE COLUMNS
  // -------------------------
  const columns: TableProps<GetAllUsersDataType>["columns"] = [
    {
      title: "#SL",
      align: "center",
      width: 40,
      render: (_, __, index) => countParPageSerialNumber(page, limit, index),
    },
    {
      title: () => (
        <Checkbox
          checked={allCheckedCurrentPage}
          indeterminate={someCheckedCurrentPage}
          onChange={(e) => handleSelectAllPage(e.target.checked)}
        />
      ),
      align: "center",
      width: 50,
      render: (_, row: any) => (
        <Checkbox
          checked={isChecked(row.id)}
          onChange={() => handleToggle(row)}
        />
      ),
    },
    {
      title: "User Name",
      dataIndex: "username",
      render: (val: string) => val || "N/A",
    },
    {
      title: "Email",
      dataIndex: "email",
      render: (val: string) => val || "N/A",
    },
    {
      title: "Department",
      dataIndex: "department",
      render: (val) => val?.name || "N/A",
    },
    {
      title: "Sub Department",
      dataIndex: "sub_department",
      render: (val) => val?.name || "N/A",
    },
    // {
    //   title: "Status",
    //   dataIndex: "status",
    //   render: (val: keyof typeof Status, record) => (
    //     <div className="flex items-center gap-2">
    //       <span className={Status[val]}>{capitalize(val || "-")}</span>
    //     </div>
    //   ),
    // },
  ];

  return (
    <CustomModal
      width={900}
      title="Employee Lists"
      open={open}
      handleClose={() => handleClose()}
      footer={null}
    >
      <CustomTable
        loading={isLoading || isFetching}
        limit={limit}
        page={page}
        columns={columns}
        total={employeeData?.data?.total ?? 0}
        dataSource={addKeyInArray(users)}
        handleLimit={handleLimit}
        handlePagination={handlePagination}
        filterSection={
          <Input
            suffix={<SearchIcon />}
            placeholder="Search by username, email"
            style={{ width: 250 }}
            allowClear
            onChange={handleSearch}
          />
        }
      />

      <div className="sticky bottom-0 bg-white">
        <Space className="w-full pt-4 pb-1" size={12}>
          <Button
            className="normal-dark-button modal-button"
            size="middle"
            onClick={() => handleClose()}
          >
            Cancel
          </Button>
          <Button
            className="modal-button"
            type="primary"
            htmlType="button"
            size="middle"
            onClick={() => {
              setData(selectedUsers);
              handleClose();
            }}
          >
            Add User
          </Button>
        </Space>
      </div>
    </CustomModal>
  );
}
